import React from 'react';
import PropTypes from 'prop-types';
import toColorString from 'polished/lib/color/toColorString';

import color from '@decisiv/design-tokens/lib/color';

import InfoCircleF from '@decisiv/iconix/lib/components/InfoCircleF';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import { H1, P } from '@decisiv/ui-components/lib/components/Typography';

import OverflowPopover from '../../shared/OverflowPopover';
import roleTag, { PERMISSION_ASSIGNMENT } from '../../shared/roleTag';
import { PanelRow } from '../../shared/styles';
import EMPTY_VALUE from '../../shared/emptyValue';
import {
  AssignmentRow,
  HeaderBar,
  HeaderIdentity,
  LastLogin,
  TitlePrefix,
} from './styles';

// How many account/role pairs sit inline before the rest collapse behind the
// counter, matching the design (Figma 787:77246): two pairs, then "1 ⌄".
export const INLINE_ASSIGNMENTS = 2;

const assignmentPair = ({ account, permission }) => (
  <>
    <Tag text={account} variant="outline" />
    {roleTag(permission)}
  </>
);

/**
 * Everything above the tabs: "Edit {name}", the account/role pairs the user holds,
 * and the ecosystem Last Login at top right.
 *
 * The breadcrumb above it is mounted by EditUser, not here — see the note there.
 */
function UserHeader({ fullName, lastLoginAt, permissionAssignments }) {
  const inline = permissionAssignments.slice(0, INLINE_ASSIGNMENTS);
  const rest = permissionAssignments.slice(INLINE_ASSIGNMENTS);

  return (
    <HeaderBar>
      <HeaderIdentity>
        <H1 style={{ margin: 0 }}>
          <TitlePrefix>Edit </TitlePrefix>
          {fullName}
        </H1>

        {permissionAssignments.length > 0 && (
          <AssignmentRow>
            {/* Index keys throughout: both halves render the same derived list,
                  which is never reordered, and a key composed from the account and
                  role can collide when either contains a hyphen — "A-B"/"C" against
                  "A"/"B-C". Keying the two branches differently would mean one of
                  them was wrong. */}
            {inline.map((assignment, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <PanelRow key={index}>{assignmentPair(assignment)}</PanelRow>
            ))}

            {rest.length > 0 && (
              <OverflowPopover
                ariaLabel={`Show all accounts and roles (${rest.length} more)`}
                count={rest.length}
              >
                {rest.map((assignment, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <PanelRow key={index}>{assignmentPair(assignment)}</PanelRow>
                ))}
              </OverflowPopover>
            )}
          </AssignmentRow>
        )}
      </HeaderIdentity>

      <LastLogin>
        <InfoCircleF color={toColorString(color.status.information.medium)} />
        <P shade={1} size="small">
          Last Login {lastLoginAt || EMPTY_VALUE}
        </P>
      </LastLogin>
    </HeaderBar>
  );
}

UserHeader.propTypes = {
  fullName: PropTypes.string,
  /** Preformatted by the server; absent when the user has never logged in. */
  lastLoginAt: PropTypes.string,
  permissionAssignments: PropTypes.arrayOf(PERMISSION_ASSIGNMENT),
};

UserHeader.defaultProps = {
  fullName: undefined,
  lastLoginAt: undefined,
  permissionAssignments: [],
};

export default UserHeader;
